import { useMemo, useRef, useState } from 'react'

import CallSplitIcon from '@mui/icons-material/CallSplit'
import Timeline from '@mui/lab/Timeline'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineContent from '@mui/lab/TimelineContent'
import TimelineDot from '@mui/lab/TimelineDot'
import TimelineItem from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import { useResizeObserver } from '@react-hookz/web'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useAppSelector } from '~/store'
import { formatDate } from '~/utils'
import FulfillmentSplitDialog from '../fulfillment/fulfillment-split'
import FulfillmentDialog from './fulfillment-dialog'
import SaleSection from './sale-section'

const InfoLabel = styled((props) => <Typography variant="body2" {...props} />)(({ theme }) => ({
    color: theme.palette.grey[700],
    fontSize: '0.7rem',
}))

const InfoValue = styled((props) => <Typography variant="body2" {...props} />)({
    fontSize: '0.8rem',
})

const FulfillButton = styled((props) => <Button color="secondary" size="small" {...props} />)({
    height: 32,
    zIndex: 1,
})

function Fulfillment({ statusesMap, baseWidth, data, canInitiateShipping, onAction }) {
    const {
        tracking_number,
        fulfillment_id,
        shipping_provider,
        fulfillment_events,
        is_shipping_label_available,
    } = data

    const timelineItemCount = Math.max(fulfillment_events.length - 1, 1)
    const timelineWidth = ((baseWidth - 100) * (timelineItemCount + 1)) / timelineItemCount

    return (
        <Stack gap={4} sx={{ mt: 1.25 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {shipping_provider && (
                        <div>
                            <img src={shipping_provider.logo_url} width={105} />
                        </div>
                    )}
                    {fulfillment_id && (
                        <div>
                            <InfoLabel>{gettext('Fulfillment ID')}</InfoLabel>
                            <InfoValue>{fulfillment_id}</InfoValue>
                        </div>
                    )}
                    {tracking_number && (
                        <div>
                            <InfoLabel>{gettext('Tracking number')}</InfoLabel>
                            <InfoValue>{tracking_number}</InfoValue>
                        </div>
                    )}
                </Box>

                {tracking_number ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <FulfillButton onClick={() => onAction('view', data)}>
                            {gettext('Details')}
                        </FulfillButton>

                        <FulfillButton
                            disabled={!is_shipping_label_available}
                            onClick={() => onAction('label', data)}
                        >
                            {gettext('View label')}
                        </FulfillButton>
                    </Box>
                ) : (
                    canInitiateShipping && (
                        <FulfillButton onClick={() => onAction('edit', data)}>
                            {gettext('Initiate Shipping')}
                        </FulfillButton>
                    )
                )}
            </Box>

            {fulfillment_events && (
                <Box sx={{ height: 100 }}>
                    <Timeline
                        style={{ height: timelineWidth }}
                        sx={{
                            transform: 'rotate(-90deg)',
                            transformOrigin: '44px 30px',
                            width: 200,
                            display: 'inline-grid',
                        }}
                    >
                        {fulfillment_events.map((event, i) => (
                            <TimelineItem key={i}>
                                <TimelineSeparator>
                                    <TimelineDot variant="outlined" color="primary" />
                                    {i === 0 || i < fulfillment_events.length - 1 ? (
                                        <TimelineConnector />
                                    ) : undefined}
                                </TimelineSeparator>

                                <TimelineContent>
                                    <Box
                                        sx={{
                                            display: 'inline-block',
                                            transform: 'rotate(90deg)',
                                            textAlign: 'center',
                                            width: '120px',
                                            height: '60px',
                                            transformOrigin: i % 2 ? '5px -46px' : '40px -12px',
                                            color: (theme) => theme.palette.grey[700],
                                            fontSize: '0.6rem',
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '0.6rem' }}>
                                            {statusesMap[event.status]}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.6rem' }}>
                                            {formatDate(event.date, { format: 'D MMM HH:mm' })}
                                        </Typography>
                                    </Box>
                                </TimelineContent>
                            </TimelineItem>
                        ))}

                        {fulfillment_events.length === 1 && (
                            <TimelineItem>
                                <TimelineSeparator>
                                    <TimelineDot variant="outlined" />
                                </TimelineSeparator>
                                <TimelineContent>
                                    <Box
                                        sx={{
                                            display: 'inline-block',
                                            transform: 'rotate(90deg)',
                                            textAlign: 'center',
                                            width: '120px',
                                            height: '60px',
                                            transformOrigin: '40px -12px',
                                            color: (theme) => theme.palette.grey[700],
                                            fontSize: '0.6rem',
                                        }}
                                    />
                                </TimelineContent>
                            </TimelineItem>
                        )}
                    </Timeline>
                </Box>
            )}
        </Stack>
    )
}

const fulfillmentsQueryKey = (saleId) => ['sale', { saleId }, 'fulfillments']
const shippingProvidersQueryKey = (storeId) => ['store', { storeId }, 'shippingProviders']

export default function Fulfillments({ sale, fulfillmentStatuses, onFulfillmentStatusChange }) {
    const { fulfillmentInitiateShippingAPIURL, fulfillmentShippingLabelURL } = useAppSelector(
        (state) => state.initial.endpoints
    )
    const containerEl = useRef()
    const [containerWidth, setContainerWidth] = useState(400)
    useResizeObserver(containerEl, (entry) => {
        setContainerWidth(Math.floor(entry.contentRect.right))
    })

    const queryClient = useQueryClient()
    const { data: fulfillmentsData, refetch: refetchFulfillments } = useQuery({
        queryKey: fulfillmentsQueryKey(sale.id),
        queryFn: async () => {
            const res = await fetch(`/endpoints/sales/${sale.id}/fulfillments/`)
            if (!res.ok) {
                throw new Error('Failed to fetch fulfillments')
            }

            const data = await res.json()

            return data.results
        },
    })

    const { data: shippingProvidersData } = useQuery({
        queryKey: shippingProvidersQueryKey(sale.shop.store.id),
        queryFn: async () => {
            const res = await fetch(`/endpoints/stores/${sale.shop.store.id}/shipping-providers/`)
            if (!res.ok) {
                throw new Error('Failed to fetch shipping providers')
            }

            const data = await res.json()

            return data.results
        },
    })

    const [fulfillmentDialogState, setFulfillmentDialogState] = useState('hide') // hide | edit | view
    const [fulfillmentSplitDialogOpen, setFulfillmentSplitDialogOpen] = useState(false)
    const [selectedFulfillmentId, setSelectedFulfillmentId] = useState(null)
    const selectedFulfillment = useMemo(() => {
        return fulfillmentsData?.find((fulfillment) => fulfillment.id === selectedFulfillmentId)
    }, [fulfillmentsData, selectedFulfillmentId])

    const fulfillmentStatusesMap = useMemo(() => {
        return fulfillmentStatuses.reduce(
            (statusMap, status) => ((statusMap[status.status] = status.label), statusMap),
            {}
        )
    }, [fulfillmentStatuses])

    const canInitiateShipping =
        sale.status === 'pending' &&
        sale?.shop?.channel?.has_integrated_shipping === false &&
        !!shippingProvidersData?.length

    function handleFulfillmentAction(action, fulfillment) {
        if (action === 'view' || action === 'edit') {
            setFulfillmentDialogState(action)
            setSelectedFulfillmentId(fulfillment.id)
        } else if (action === 'label') {
            window.open(
                `${fulfillmentShippingLabelURL.replace('fulfillment_pk', fulfillment.id)}`,
                '_blank'
            )
        }
    }

    function handleDialogClose(state) {
        setFulfillmentDialogState('hide')
        if (state === 'shipping_initiated') {
            setSelectedFulfillmentId(null)
            refetchFulfillments()
            onFulfillmentStatusChange()
        }
    }

    function handleFulfillmentSplitDialogClose(data) {
        setFulfillmentSplitDialogOpen(false)
        if ('fulfillments' in data) {
            queryClient.setQueryData(fulfillmentsQueryKey(sale.id), data.fulfillments)
        }
    }

    return (
        <SaleSection
            ref={containerEl}
            label={gettext('Fulfillments')}
            action={
                sale.status === 'pending' &&
                sale.items_count > 1 && (
                    <Tooltip title="Split fulfillments">
                        <IconButton
                            size="small"
                            style={{ zIndex: 1 }}
                            onClick={() => setFulfillmentSplitDialogOpen(true)}
                        >
                            <CallSplitIcon />
                        </IconButton>
                    </Tooltip>
                )
            }
        >
            {fulfillmentsData?.map((item) => (
                <Fulfillment
                    key={item.id}
                    data={item}
                    statusesMap={fulfillmentStatusesMap}
                    baseWidth={containerWidth}
                    canInitiateShipping={canInitiateShipping}
                    onAction={handleFulfillmentAction}
                />
            ))}
            {fulfillmentDialogState !== 'hide' && (
                <FulfillmentDialog
                    initiateShippingUrl={fulfillmentInitiateShippingAPIURL}
                    open={fulfillmentDialogState !== 'hide'}
                    mode={fulfillmentDialogState === 'edit' ? 'edit' : 'view'}
                    fulfillment={selectedFulfillment}
                    shippingProviders={shippingProvidersData}
                    sale={sale}
                    onClose={handleDialogClose}
                />
            )}
            {fulfillmentSplitDialogOpen && (
                <FulfillmentSplitDialog
                    open={fulfillmentSplitDialogOpen}
                    sale={sale}
                    onClose={handleFulfillmentSplitDialogClose}
                />
            )}
        </SaleSection>
    )
}
